import { useCallback, useMemo, useRef, useState } from 'react';
import { useCurrentUser, useNavigationHelper, useOfflineModeManager } from 'FrontRoyalAngular';
import { canLaunchStream as evaluateStreamAccess, hasCompletedPrerequisites } from 'ContentAccess';
import { examVerificationRecordForExamVerificationSession, mustVerifyIdentityToLaunchStream } from 'ExamVerification';
import { getCurriculumStatus, type CurrentUser } from 'Users';
import { type LessonForStreamDashboard, type StreamForDashboard, type StreamForStreamDashboard } from 'Lessons';
import {
    getKeyTermsByLesson,
    getStreamSummaries,
    getTimeLimitHours,
    hasTimeLimit,
    isStreamComplete,
    isStreamStarted,
} from '../selectors';
import { toStreamForDashboard } from '../toStreamForDashboard';
import { useCertificateBounce } from './useCertificateBounce';
import { useCertificateDownload } from './useCertificateDownload';
import { useCourseTalk } from './useCourseTalk';
import { useIguanaUserFlags } from './useIguanaUserFlags';
import { useIsBelowMdViewport, useIsMobileViewport } from './useIsMobileViewport';
import { useCertificateImageSrc } from './useCertificateImageSrc';
import { useKeyTermsReveal } from './useKeyTermsReveal';
import { useLaunchLesson } from './useLaunchLesson';
import { useLessonAccess } from './useLessonAccess';
import { useOpenSummary } from './useOpenSummary';
import { useShareStream } from './useShareStream';
import { useStreamBookmark } from './useStreamBookmark';
import { useStreamDashboardHeader } from './useStreamDashboardHeader';
import { useStripFormatting } from './useStripFormatting';
import { useUploadUnofficialTranscriptsModal } from './useUploadUnofficialTranscriptsModal';

// Everything StreamDashboardPage needs, so the component is markup only.

/*
    Forces the student off the page when prerequisites are unmet AND the stream cannot launch. Both must be
    true: the prerequisite check alone covers different playlists than canLaunch. leaveForDashboard is the
    only exit.
*/
function usePrerequisiteGate(
    streamForDashboard: StreamForDashboard,
    streamProgress: StreamForStreamDashboard['lessonStreamsProgress'],
    streamAccess: { canLaunch: boolean; reasonTitle: string | null; reasonMessage: string | null },
) {
    const { loadRoute } = useNavigationHelper();

    const prerequisitesNotMet = useMemo(
        () => !streamAccess.canLaunch && !hasCompletedPrerequisites({ stream: streamForDashboard, streamProgress }),
        [streamAccess.canLaunch, streamForDashboard, streamProgress],
    );

    return {
        prerequisitesNotMet,
        prerequisitesReasonTitle: streamAccess.reasonTitle,
        prerequisitesReasonMessage: streamAccess.reasonMessage,
        leaveForDashboard: useCallback(() => loadRoute('/dashboard'), [loadRoute]),
    };
}

function useStreamAccess(stream: StreamForStreamDashboard) {
    const currentUser = useCurrentUser();
    const getLessonAccess = useLessonAccess(stream);
    const streamForDashboard = useMemo(() => toStreamForDashboard(stream), [stream]);

    const canLaunchLesson = useCallback(
        (lesson: LessonForStreamDashboard) => getLessonAccess(lesson).canLaunch,
        [getLessonAccess],
    );

    const streamAccess = useMemo(
        () =>
            evaluateStreamAccess({
                stream: streamForDashboard,
                user: currentUser,
                streamProgress: stream.lessonStreamsProgress,
            }),
        [streamForDashboard, currentUser, stream.lessonStreamsProgress],
    );

    const canLaunchThisStream = streamAccess.canLaunch;

    const mustVerifyIdentity = useMemo(
        () =>
            mustVerifyIdentityToLaunchStream({
                user: currentUser,
                stream: streamForDashboard,
                streamProgress: stream.lessonStreamsProgress,
            }),
        [currentUser, streamForDashboard, stream.lessonStreamsProgress],
    );

    const hasExamVerificationRecord = useMemo(
        () => !!currentUser && !!examVerificationRecordForExamVerificationSession(currentUser, streamForDashboard),
        [currentUser, streamForDashboard],
    );

    return {
        getLessonAccess,
        canLaunchLesson,
        canLaunchThisStream,
        ...usePrerequisiteGate(streamForDashboard, stream.lessonStreamsProgress, streamAccess),
        mustVerifyIdentity,
        hasExamVerificationRecord,
    };
}

/*
    Key terms, and which summaries belong to the stream and to each lesson. getStreamSummaries returns
    nothing without a user, and nothing when the stream cannot be launched, so both cases fall out here.
*/
function useStreamDashboardContent(stream: StreamForStreamDashboard, isMobile: boolean, canLaunch: boolean) {
    const currentUser = useCurrentUser();
    const stripFormatting = useStripFormatting();

    const keyTermsByLesson = useMemo(() => getKeyTermsByLesson(stream, stripFormatting), [stream, stripFormatting]);
    const keyTermCount = keyTermsByLesson.reduce((count, group) => count + group.keyTerms.length, 0);
    const reveal = useKeyTermsReveal(keyTermCount, isMobile);

    const summariesForStream = useMemo(
        () => getStreamSummaries(stream, currentUser, canLaunch),
        [stream, currentUser, canLaunch],
    );

    const summariesForLesson = useCallback(
        (lesson: LessonForStreamDashboard) => summariesForStream.filter(summary => summary.lessons.includes(lesson.id)),
        [summariesForStream],
    );

    return {
        keyTermsByLesson,
        keyTermCount,
        keyTermsReady: reveal.isReady,
        hasHiddenKeyTerms: reveal.hasHiddenKeyTerms,
        showAllKeyTerms: reveal.showAllKeyTerms,
        summariesForStream,
        summariesForLesson,
        openSummary: useOpenSummary(stream, summariesForStream),
    };
}

/*
    The three modals the page can open. The timed-exam one remembers a pending launch, so it can resume
    it once the student confirms.
*/
function useStreamDashboardModalState() {
    const [upsellOpen, setUpsellOpen] = useState(false);
    const [lockedMessage, setLockedMessage] = useState<string | null>(null);
    const [pendingTimedLaunch, setPendingTimedLaunch] = useState<{
        lesson: LessonForStreamDashboard;
        linkId: string;
    } | null>(null);

    return { upsellOpen, setUpsellOpen, lockedMessage, setLockedMessage, pendingTimedLaunch, setPendingTimedLaunch };
}

/*
    Mirrors tryToLaunchLesson, keeping its three gates in order: a visitor with no account gets the upsell
    instead of a lesson, a locked lesson stops there, and an unstarted timed exam asks for confirmation
    first.
*/
function useTryToLaunchLesson(
    stream: StreamForStreamDashboard,
    access: ReturnType<typeof useStreamAccess>,
    modals: ReturnType<typeof useStreamDashboardModalState>,
    launchLesson: ReturnType<typeof useLaunchLesson>,
) {
    return useCallback(
        (lesson: LessonForStreamDashboard, linkId: string) => {
            const lessonAccess = access.getLessonAccess(lesson);

            if (lessonAccess.reason === 'no_user') {
                modals.setUpsellOpen(true);
                return;
            }
            if (!lessonAccess.canLaunch) return;

            if (hasTimeLimit(stream) && !isStreamStarted(stream)) {
                modals.setPendingTimedLaunch({ lesson, linkId });
                return;
            }

            launchLesson(lesson, linkId);
        },
        [access, modals, stream, launchLesson],
    );
}

/*
    Share tiles are hidden offline and for blue ocean students. The sidebar bookmark needs a user. The
    main-column bookmark shows for visitors but is disabled.
*/
function getShareAndBookmarkVisibility({
    stream,
    currentUser,
    blueOcean,
    canShare,
    inOfflineMode,
}: {
    stream: StreamForStreamDashboard;
    currentUser: CurrentUser | null;
    blueOcean: boolean | undefined;
    canShare: boolean | undefined;
    inOfflineMode: boolean;
}) {
    const shareable = !inOfflineMode && (!currentUser || !!canShare);
    const bookmarkable = !stream.exam && !blueOcean && !inOfflineMode;

    return {
        canShare: shareable,
        showBookmark: !!currentUser && bookmarkable,
        showMainColumnBookmark: bookmarkable,
        showMainColumnShare: shareable && !blueOcean,
    };
}

export function useStreamDashboardPage(stream: StreamForStreamDashboard) {
    const currentUser = useCurrentUser();
    const offlineModeManager = useOfflineModeManager();
    const isMobile = useIsMobileViewport();
    const isBelowMd = useIsBelowMdViewport();
    const { canShare, blueOcean } = useIguanaUserFlags();
    const inOfflineMode = offlineModeManager.inOfflineMode;

    const access = useStreamAccess(stream);
    const content = useStreamDashboardContent(stream, isMobile, access.canLaunchThisStream);
    const modals = useStreamDashboardModalState();

    const launchLesson = useLaunchLesson(stream);
    const shareStream = useShareStream(stream);
    const showTranscriptsReminder = useUploadUnofficialTranscriptsModal(stream);
    const { courseTalkId } = useCourseTalk(stream.id);
    const certificateDownload = useCertificateDownload(stream);
    const [userClickedToUnhideSchedule, setUserClickedToUnhideSchedule] = useState(false);

    useStreamDashboardHeader(stream);

    const { favorite, toggleBookmark } = useStreamBookmark(stream);

    const certificateRef = useRef<HTMLDivElement>(null);
    useCertificateBounce(certificateRef, stream.id, showTranscriptsReminder);

    const tryToLaunchLesson = useTryToLaunchLesson(stream, access, modals, launchLesson);
    const certificate = useCertificateState(stream, certificateDownload);

    return {
        ...access,
        ...content,
        ...modals,
        ...certificate,
        currentUser,
        isMobile,
        certificateRef,

        favorite,
        ...getShareAndBookmarkVisibility({ stream, currentUser, blueOcean, canShare, inOfflineMode }),
        courseTalkId,

        // Resource links are dead while offline, and for a student whose curriculum status is failed.
        resourcesDisabled: offlineModeManager.inOfflineMode || getCurriculumStatus(currentUser) === 'failed',

        /*
            Read through getTimeLimitHours, not off the payload, so a student with the extendExamTime
            accommodation sees their extended allowance. Every surface that prints an hour count --
            the timer, the schedule box and the pre-launch modal -- takes this value.
        */
        timeLimitHours: getTimeLimitHours(stream, currentUser),

        // Use the 992px breakpoint, not 768px. It matches the md:hidden main-column copy of the box.
        showSchedule: !isBelowMd || !isStreamStarted(stream) || userClickedToUnhideSchedule,
        onShowSchedule: () => setUserClickedToUnhideSchedule(true),

        tryToLaunchLesson,
        launchLesson,
        shareStream,
        toggleBookmark,
    };
}

/*
    Whether to show the certificate, and the download state that goes with it. Offline hides it, because
    the PDF is signed against the network.
*/
function useCertificateState(
    stream: StreamForStreamDashboard,
    certificateDownload: ReturnType<typeof useCertificateDownload>,
) {
    const offlineModeManager = useOfflineModeManager();

    const certificateImageSrc = useCertificateImageSrc(stream);
    const { canDownloadStreamCertificateOrAddToLinkedInProfile } = useIguanaUserFlags();

    return {
        certificateImageSrc,
        showCertificate: !!certificateImageSrc && isStreamComplete(stream) && !offlineModeManager.inOfflineMode,
        isDownloading: certificateDownload.isDownloading,
        allowDownload: certificateDownload.allowDownload,

        /*
            The LinkedIn button's own gate is only Cordova and exams. The permission check lived in the
            template, as `ng-if="currentUser.canDownloadStreamCertificateOrAddToLinkedInProfile"`.
        */
        allowLinkedIn: !!canDownloadStreamCertificateOrAddToLinkedInProfile,
        downloadCertificate: certificateDownload.downloadCertificate,
    };
}
